import React, { useState, useEffect } from 'react';

// Domineering Game Component
// Player 1 (Horizontal) places dominoes covering two adjacent cells in the same row.
// Player 2 (Vertical) places dominoes covering two adjacent cells in the same column.
// A player loses when they have no valid moves on their turn.
const Domineering = () => {
	const [gameStarted, setGameStarted] = useState(false);
	const [playerTypes, setPlayerTypes] = useState({ 1: 'human', 2: 'human' });
	const [board, setBoard] = useState(Array(64).fill(null)); // 8x8 = 64 cells, null | 1 | 2
	const [currentPlayer, setCurrentPlayer] = useState(1); // 1 = Horizontal, 2 = Vertical
	const [winner, setWinner] = useState(null);

	/**
	 * Returns true if placing a domino at `index` for `player` is valid.
	 * Player 1 (Horizontal): covers (row, col) and (row, col+1)
	 * Player 2 (Vertical):   covers (row, col) and (row+1, col)
	 */
	const isValidMove = (index, player, currentBoard) => {
		const row = Math.floor(index / 8);
		const col = index % 8;

		if (player === 1) {
			// Horizontal: need col+1 within bounds and both cells empty
			if (col >= 7) return false;
			const right = index + 1;
			return currentBoard[index] === null && currentBoard[right] === null;
		} else {
			// Vertical: need row+1 within bounds and both cells empty
			if (row >= 7) return false;
			const below = index + 8;
			return currentBoard[index] === null && currentBoard[below] === null;
		}
	};

	/**
	 * Returns all valid move starting indices for a given player.
	 */
	const getValidMoves = (player, currentBoard) => {
		const moves = [];
		for (let i = 0; i < 64; i++) {
			if (isValidMove(i, player, currentBoard)) {
				moves.push(i);
			}
		}
		return moves;
	};

	/**
	 * Apply a move: mark both cells for the player and return the new board.
	 */
	const applyMove = (index, player, currentBoard) => {
		const newBoard = [...currentBoard];
		newBoard[index] = player;
		if (player === 1) {
			newBoard[index + 1] = player;
		} else {
			newBoard[index + 8] = player;
		}
		return newBoard;
	};

	/**
	 * Handle a cell click from a human player.
	 */
	const handleClick = (index) => {
		if (winner) return;
		if (playerTypes[currentPlayer] !== 'human') return;
		if (!isValidMove(index, currentPlayer, board)) return;

		const newBoard = applyMove(index, currentPlayer, board);
		setBoard(newBoard);

		const nextPlayer = currentPlayer === 1 ? 2 : 1;
		if (getValidMoves(nextPlayer, newBoard).length === 0) {
			setWinner(currentPlayer);
		} else {
			setCurrentPlayer(nextPlayer);
		}
	};

	/**
	 * Reset the game to the configuration screen.
	 */
	const resetGame = () => {
		setBoard(Array(64).fill(null));
		setCurrentPlayer(1);
		setWinner(null);
		setGameStarted(false);
	};

	/**
	 * Start the game with chosen player types.
	 */
	const startGame = () => {
		setGameStarted(true);
	};

	/**
	 * Handle player type toggle.
	 */
	const handlePlayerTypeChange = (player, type) => {
		setPlayerTypes(prev => ({ ...prev, [player]: type }));
	};

	// Computer player: pick a random valid move after a short delay.
	useEffect(() => {
		if (!gameStarted || winner) return;
		if (playerTypes[currentPlayer] !== 'computer') return;

		const timeoutId = setTimeout(() => {
			const moves = getValidMoves(currentPlayer, board);
			if (moves.length === 0) {
				// No moves — opponent wins (should already be caught, but guard here)
				const nextPlayer = currentPlayer === 1 ? 2 : 1;
				setWinner(nextPlayer);
				return;
			}
			const move = moves[Math.floor(Math.random() * moves.length)];
			const newBoard = applyMove(move, currentPlayer, board);
			setBoard(newBoard);

			const nextPlayer = currentPlayer === 1 ? 2 : 1;
			if (getValidMoves(nextPlayer, newBoard).length === 0) {
				setWinner(currentPlayer);
			} else {
				setCurrentPlayer(nextPlayer);
			}
		}, 500);

		return () => clearTimeout(timeoutId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPlayer, gameStarted, winner, board, playerTypes]);

	/**
	 * Current status message.
	 */
	const getStatus = () => {
		if (winner) {
			const name = winner === 1 ? 'Player 1 (Horizontal)' : 'Player 2 (Vertical)';
			return `Winner: ${name}!`;
		}
		const name = currentPlayer === 1 ? 'Player 1 (Horizontal)' : 'Player 2 (Vertical)';
		return `Current Turn: ${name}`;
	};

	// ── Configuration screen ────────────────────────────────────────────────
	if (!gameStarted) {
		return (
			<div className="game-container">
				<h1 className="game-title">Dominório</h1>
				<h2 className="config-subtitle">Configuração de Jogadores</h2>
				<div className="player-config">
					<div className="player-config-item">
						<h3>Jogador 1 (Horizontal)</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes[1] === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(1, 'human')}
							>
								Humano
							</button>
							<button
								className={`player-type-button ${playerTypes[1] === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(1, 'computer')}
							>
								Computador
							</button>
						</div>
					</div>
					<div className="player-config-item">
						<h3>Jogador 2 (Vertical)</h3>
						<div className="player-type-selector">
							<button
								className={`player-type-button ${playerTypes[2] === 'human' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(2, 'human')}
							>
								Humano
							</button>
							<button
								className={`player-type-button ${playerTypes[2] === 'computer' ? 'selected' : ''}`}
								onClick={() => handlePlayerTypeChange(2, 'computer')}
							>
								Computador
							</button>
						</div>
					</div>
				</div>
				<button className="new-game-button" onClick={startGame}>
					Iniciar Jogo
				</button>
			</div>
		);
	}

	// ── Game board ──────────────────────────────────────────────────────────
	const isHumanTurn = playerTypes[currentPlayer] === 'human';
	const validMoveSet = new Set(!winner && isHumanTurn ? getValidMoves(currentPlayer, board) : []);

	return (
		<div className="game-container">
			<h1 className="game-title">Dominório</h1>
			<div className={`game-status ${winner ? 'winner' : ''}`}>
				{getStatus()}
			</div>
			<svg
				viewBox="0 0 800 800"
				className="domineering-svg"
			>
				{board.map((cell, index) => {
					const row = Math.floor(index / 8);
					const col = index % 8;
					const x = col * 100;
					const y = row * 100;
					const isValid = validMoveSet.has(index);

					// Cell fill colour
					let fill;
					if (cell === 1) {
						fill = '#93c5fd'; // blue for Player 1
					} else if (cell === 2) {
						fill = '#fca5a5'; // red for Player 2
					} else if (isValid) {
						fill = '#d1fae5'; // green tint for valid moves
					} else {
						fill = '#f3f4f6'; // neutral
					}

					const isClickable = isValid && !winner;

					return (
						<g
							key={index}
							onClick={() => isClickable && handleClick(index)}
							style={{ cursor: isClickable ? 'pointer' : 'default' }}
						>
							<rect
								x={x + 2}
								y={y + 2}
								width={96}
								height={96}
								rx={4}
								fill={fill}
								stroke={isValid ? '#6ee7b7' : '#d1d5db'}
								strokeWidth={isValid ? 2 : 1}
							/>
							{/* Player label in occupied cells */}
							{cell !== null && (
								<text
									x={x + 50}
									y={y + 56}
									textAnchor="middle"
									fontSize="22"
									fontWeight="bold"
									fill={cell === 1 ? '#1d4ed8' : '#b91c1c'}
								>
									{cell === 1 ? 'H' : 'V'}
								</text>
							)}
						</g>
					);
				})}
			</svg>
			<button className="new-game-button" onClick={resetGame}>
				Novo Jogo
			</button>
		</div>
	);
};

export default Domineering;
